import { ApiError } from 'next/dist/server/api-utils';
import { logger } from '@finance/shared/dist/utils/logger';
import { safeJsonStringify } from '@finance/shared/dist/utils/safe-json';
import { redactSensitiveLogData } from '@finance/shared/dist/utils/log-redaction';
import { StatusCodes } from 'http-status-codes';

interface FinanceErrorParams {
    message: string;
    obj?: object;
    statusCode?: StatusCodes;
    reportError?: boolean;
}

export default class FinanceError extends ApiError {
    constructor({ message, obj, statusCode = StatusCodes.INTERNAL_SERVER_ERROR, reportError = false }: FinanceErrorParams) {
        super(statusCode, message);
        // `obj` frequently carries a caught Axios/Unit error whose config holds a bearer token
        const logObject = redactSensitiveLogData({ errorType: this.getErrorType(), error: this, ...obj }) as Record<string, unknown>;

        if (!reportError && statusCode >= StatusCodes.BAD_REQUEST && statusCode < StatusCodes.INTERNAL_SERVER_ERROR) {
            logger.debug(logObject, message);
        } else {
            logger.error(logObject, message);
        }
    }

    public getErrorType() {
        return this.constructor.name;
    }

    private static isError(error: unknown): error is Error {
        return typeof error === 'object' && error !== null && 'message' in error;
    }

    public static isFinanceError(error: unknown): error is FinanceError {
        return this.isError(error) && 'statusCode' in error;
    }

    public static toFinanceError(error: unknown): FinanceError {
        if (this.isFinanceError(error)) {
            return error;
        }

        return new FinanceError({
            message: this.isError(error) ? (error as Error).message : safeJsonStringify(error)
        });
    }
}
